import { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  ChevronLeft,
  ChevronRight,
  Eye,
  FileText,
  User,
  Users as UsersIcon,
} from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  type TopPageEventRecord,
  type TopPageEventsType,
  type TopPageItem,
  useTopPageEvents,
  useTopPages,
} from '@/hooks/useDashboardAnalytics';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

interface TopPagesWidgetProps {
  days?: number;
  limit?: number;
  className?: string;
}

interface TopPageEventsDialogState {
  page: TopPageItem;
  type: TopPageEventsType;
}

interface TopPageEventsDialogProps {
  state: TopPageEventsDialogState | null;
  days: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const EVENTS_PAGE_SIZE = 20;

function formatNumber(n: number): string {
  return new Intl.NumberFormat('ru-RU').format(n);
}

const LOCALE_SEGMENTS = new Set(['ru', 'en', 'uz']);

const ROUTE_LABELS: Record<string, string> = {
  dashboard: 'Главная',
  'my-courses': 'Моё обучение',
  courses: 'Курсы',
  club: 'Masters Club',
  content: 'Контент',
  articles: 'Статьи',
  teachers: 'Преподаватели',
  planner: 'Ежедневник',
  games: 'Игры',
  'card-color': 'Угадай цвет',
  'guess-number': 'Угадай число',
  lobby: 'Лобби',
  play: 'Игра',
  chat: 'Чат',
  subscriptions: 'Подписки',
  'channel-passes': 'Каналы',
  bonuses: 'Бонусы',
  settings: 'Настройки',
  privacy: 'Конфиденциальность',
  profile: 'Профиль',
  notifications: 'Уведомления',
  payments: 'Платежи',
  checkout: 'Оплата',
  success: 'Заказ оформлен',
  lessons: 'Уроки',
  posts: 'Посты',
  meetings: 'Встречи',
  members: 'Участники',
  info: 'Информация',
  menu: 'Меню',
  'order-success': 'Заказ оформлен',
  pricing: 'Тарифы',
  family: 'Семейная подписка',
  legal: 'Документы',
  login: 'Вход',
  library: 'Библиотека',
  reader: 'Чтение',
  onboarding: 'Настройка',
  e2ee: 'Шифрование',
  intro: 'Введение',
  pin: 'PIN',
  recovery: 'Восстановление',
  security: 'Безопасность',
  devices: 'Устройства',
  search: 'Поиск',
  livestreams: 'Прямые трансляции',
};

function getPathname(raw: string): string {
  try {
    return new URL(raw, 'https://analytics.local').pathname;
  } catch {
    return raw.split(/[?#]/, 1)[0] ?? raw;
  }
}

function getPathSegments(raw: string): string[] {
  return getPathname(raw)
    .split('/')
    .filter((segment) => segment.length > 0)
    .filter((segment, index) => index !== 0 || !LOCALE_SEGMENTS.has(segment));
}

function decodePathSegment(segment: string): string {
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
}

function formatSlug(segment: string): string {
  const decoded = decodePathSegment(segment);
  return decoded
    .split(/[-_]+/)
    .filter((part) => part.length > 0)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function formatNumericSegment(segment: string, previousSegment: string | undefined): string {
  if (previousSegment === 'lessons') {return `Урок #${segment}`;}
  if (previousSegment === 'livestreams') {return `Трансляция #${segment}`;}
  if (previousSegment === 'chat') {return `Чат #${segment}`;}
  return `#${segment}`;
}

function formatRouteSegment(segment: string, previousSegment: string | undefined): string {
  if (/^\d+$/.test(segment)) {
    return formatNumericSegment(segment, previousSegment);
  }
  return ROUTE_LABELS[segment] ?? formatSlug(segment);
}

function formatPageTitle(raw: string): string {
  const segments = getPathSegments(raw);
  if (segments.length === 0) {return 'Главная';}
  return segments
    .map((segment, index) => formatRouteSegment(segment, segments[index - 1]))
    .join(' / ');
}

function compactPath(raw: string, maxLength = 48): string {
  const path = getPathname(raw);
  if (path.length <= maxLength) {return path;}
  const segments = path.split('/');
  if (segments.length <= 3) {
    return `${path.slice(0, maxLength - 1)}…`;
  }
  const head = segments.slice(0, 2).join('/');
  const tail = segments.at(-1) ?? '';
  return `${head}/…/${tail}`;
}

function formatDateTime(dateString: string): string {
  return new Date(dateString).toLocaleString('ru-RU', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function getUserDisplayName(record: TopPageEventRecord): string {
  if (!record.user) {
    return `Анонимная сессия #${record.sessionId}`;
  }
  const fullName = [record.user.firstName, record.user.lastName].filter(Boolean).join(' ');
  if (fullName.length > 0) {
    return fullName;
  }
  if (record.user.username) {
    return `@${record.user.username}`;
  }
  return `User #${record.user.id}`;
}

function getTopPageEventsTotal(state: TopPageEventsDialogState | null): number {
  if (!state) {
    return 0;
  }
  if (state.type === 'visitors') {
    return state.page.uniqueVisitors;
  }
  return state.page.views;
}

function TopPageEventsDialog({ state, days, open, onOpenChange }: TopPageEventsDialogProps) {
  const [page, setPage] = useState(1);
  const selectedPage = state?.page ?? null;
  const selectedType = state?.type ?? null;
  const total = getTopPageEventsTotal(state);
  const totalPages = Math.max(1, Math.ceil(total / EVENTS_PAGE_SIZE));
  const { data: records = [], isLoading } = useTopPageEvents(
    selectedPage?.page ?? null,
    selectedType,
    days,
    page,
    EVENTS_PAGE_SIZE,
    open,
  );

  useEffect(() => {
    if (open) {
      setPage(1);
    }
  }, [open, selectedPage?.page, selectedType]);

  const Icon = selectedType === 'visitors' ? UsersIcon : Eye;
  const title = selectedType === 'visitors' ? 'Посетители страницы' : 'Просмотры страницы';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[82vh] sm:max-w-2xl flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 pr-8">
            <Icon className="h-5 w-5" />
            <span className="min-w-0 truncate">{title}</span>
            <Badge variant="secondary">{formatNumber(total)}</Badge>
          </DialogTitle>
          {selectedPage && (
            <div className="space-y-1 text-sm">
              <p className="font-medium text-foreground">{formatPageTitle(selectedPage.page)}</p>
              <p className="break-all text-muted-foreground">{selectedPage.page}</p>
              <p className="text-xs text-muted-foreground">За последние {days} дней</p>
            </div>
          )}
        </DialogHeader>

        <div className="min-h-0 flex-1 overflow-y-auto">
          <TopPageEventsList records={records} isLoading={isLoading} />
        </div>

        {totalPages > 1 && (
          <div className="flex items-center justify-between border-t pt-3">
            <Button
              variant="outline"
              size="icon"
              disabled={page <= 1}
              onClick={() => setPage((currentPage) => Math.max(1, currentPage - 1))}
              aria-label="Предыдущая страница событий"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm text-muted-foreground">{page} / {totalPages}</span>
            <Button
              variant="outline"
              size="icon"
              disabled={page >= totalPages}
              onClick={() => setPage((currentPage) => Math.min(totalPages, currentPage + 1))}
              aria-label="Следующая страница событий"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function TopPageEventsList({
  records,
  isLoading,
}: {
  records: TopPageEventRecord[];
  isLoading: boolean;
}) {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-10 text-sm text-muted-foreground">
        Загрузка...
      </div>
    );
  }

  if (records.length === 0) {
    return (
      <div className="flex items-center justify-center py-10 text-sm text-muted-foreground">
        Нет событий за выбранный период
      </div>
    );
  }

  return (
    <div className="divide-y">
      {records.map((record) => (
        <div key={record.id} className="grid grid-cols-[auto_minmax(0,1fr)_auto] gap-3 px-1 py-3">
          {record.user?.photoUrl ? (
            <img
              src={record.user.photoUrl}
              alt=""
              className="h-9 w-9 rounded-full object-cover"
            />
          ) : (
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-muted">
              <User className="h-4 w-4 text-muted-foreground" />
            </div>
          )}
          <div className="min-w-0">
            <p className="truncate text-sm font-medium">{getUserDisplayName(record)}</p>
            <p className="mt-0.5 truncate text-xs text-muted-foreground">
              Сессия #{record.sessionId}
            </p>
          </div>
          <time className="whitespace-nowrap text-xs text-muted-foreground">
            {formatDateTime(record.createdAt)}
          </time>
        </div>
      ))}
    </div>
  );
}

export function TopPagesWidget({ days = 30, limit = 10, className }: TopPagesWidgetProps) {
  const { data, isLoading } = useTopPages(days, limit);
  const [eventsDialog, setEventsDialog] = useState<TopPageEventsDialogState | null>(null);

  const pages = useMemo(() => data?.pages ?? [], [data?.pages]);

  const maxViews = useMemo(() => {
    let max = 0;
    for (const p of pages) {
      if (p.views > max) {max = p.views;}
    }
    return max;
  }, [pages]);

  if (isLoading) {
    return (
      <Card className={className}>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <FileText className="h-4 w-4" />
            Популярные страницы
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {Array.from({ length: 5 }, (_, i) => (
            <Skeleton key={`page-skeleton-${i}`} className="h-10 w-full" />
          ))}
        </CardContent>
      </Card>
    );
  }

  if (pages.length === 0) {
    return (
      <Card className={className}>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <FileText className="h-4 w-4" />
            Популярные страницы
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground text-center py-8">
            Нет данных о посещениях страниц
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card className={className}>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <FileText className="h-4 w-4" />
            Популярные страницы
          </CardTitle>
          <p className="text-xs text-muted-foreground mt-1">
            За последние {days} дней
          </p>
        </CardHeader>
        <CardContent className="p-0">
          <ScrollArea className="h-[300px] overflow-hidden">
            <TooltipProvider delayDuration={150}>
              <div className="space-y-1 px-4 pb-4">
                {pages.map((page) => {
                  const barWidth = maxViews === 0 ? 0 : (page.views / maxViews) * 100;
                  const pageTitle = formatPageTitle(page.page);
                  const pagePath = compactPath(page.page);
                  return (
                    <Tooltip key={page.page}>
                      <TooltipTrigger asChild>
                        <div className="relative overflow-hidden rounded-md px-2 py-2 hover:bg-muted/50 transition-colors">
                          <div
                            className="absolute inset-y-0 left-0 bg-blue-500/10 rounded-md"
                            style={{ width: `${barWidth}%` }}
                          />
                          <div className="relative grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3 text-xs">
                            <div className="min-w-0">
                              <p className="truncate font-medium text-foreground">
                                {pageTitle}
                              </p>
                              <p className="mt-0.5 truncate text-[11px] text-muted-foreground">
                                {pagePath}
                              </p>
                            </div>
                            <div className="flex flex-shrink-0 items-center gap-1 text-muted-foreground tabular-nums">
                              <button
                                type="button"
                                className="inline-flex items-center gap-1 rounded-sm px-1 py-0.5 transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  setEventsDialog({ page, type: 'views' });
                                }}
                                aria-label={`Показать просмотры страницы ${pageTitle}`}
                              >
                                <Eye className="h-3 w-3" />
                                {formatNumber(page.views)}
                              </button>
                              <button
                                type="button"
                                className="inline-flex items-center gap-1 rounded-sm px-1 py-0.5 transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  setEventsDialog({ page, type: 'visitors' });
                                }}
                                aria-label={`Показать посетителей страницы ${pageTitle}`}
                              >
                                <UsersIcon className="h-3 w-3" />
                                {formatNumber(page.uniqueVisitors)}
                              </button>
                            </div>
                          </div>
                        </div>
                      </TooltipTrigger>
                      <TooltipContent side="top" align="center">
                        <p className="font-medium break-all max-w-xs">{page.page}</p>
                        <p className="text-muted-foreground">
                          Просмотров: {formatNumber(page.views)} · Посетителей:{' '}
                          {formatNumber(page.uniqueVisitors)}
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  );
                })}
              </div>
            </TooltipProvider>
          </ScrollArea>
        </CardContent>
      </Card>
      <TopPageEventsDialog
        state={eventsDialog}
        days={days}
        open={eventsDialog !== null}
        onOpenChange={(open) => {
          if (!open) {
            setEventsDialog(null);
          }
        }}
      />
    </>
  );
}
